import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  TextField,
  Box,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTheme, alpha } from "@mui/material/styles";
import { useSelector } from "react-redux";

// Two admin actions used to live side by side in this modal's action row: a benign
// "Copy Workout to User" and a red "Delete Workout from User" that fired a permanent
// delete on another user's account with NO confirmation (issue #180). One mis-click
// was unrecoverable.
//
// Now:
//  - The destructive action lives in its own bordered danger zone inside the body,
//    with its own target-email field, so it is never adjacent to the safe button.
//  - It is ARMED by typing the target email exactly, following the pattern PR #179
//    established in ResetAccount.js. A restatement you can click past is not a guard
//    when the blast radius is another user's workout.
//  - Both handlers return { ok, message } and the outcome is rendered here, so a
//    failure can no longer look identical to a success.
//  - The environment the call will hit is stated up front and again inside the
//    confirm, because these calls now honour the Prod/Staging toggle.
const AdminCopyWorkoutToUserModal = ({
  open,
  handleClose,
  handleAdminSubmitCopyWorkoutToUser,
  handleAdminDeleteWorkoutFromUser,
}) => {
  const theme = useTheme();
  // Read the selected admin environment straight from the store, as every other
  // /admin surface does, rather than prop-drilling it through the presentational
  // Workout tree. Same store, same render as the container that fires the call, so
  // the environment shown here is the environment that gets hit. This is a label
  // only — the request itself is built from adminRequest in WorkoutContainer — so
  // select the scalar rather than adminRequest's fresh object, which would re-render
  // this modal on every dispatch.
  const adminEnv = useSelector((state) => state.adminEnvSlice.env);

  // Copy and delete each own their target email. Sharing one field between a benign
  // and an irreversible action is part of what made a mis-click cheap.
  const [copyEmail, setCopyEmail] = useState("");
  const [copyEmailError, setCopyEmailError] = useState("");

  const [deleteEmail, setDeleteEmail] = useState("");
  const [deleteEmailError, setDeleteEmailError] = useState("");
  const [workoutToDelete, setWorkoutToDelete] = useState("");
  const [workoutIdError, setWorkoutIdError] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  // The arming input inside the confirm dialog — must match deleteEmail exactly.
  const [confirmEmail, setConfirmEmail] = useState("");

  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const trimmedDeleteEmail = deleteEmail.trim();
  const trimmedWorkoutId = workoutToDelete.trim();
  // Case-insensitive because email is case-insensitive in practice, but otherwise
  // exact — no slack beyond the outer whitespace the operator cannot see.
  const armed =
    trimmedDeleteEmail !== "" &&
    confirmEmail.trim().toLowerCase() === trimmedDeleteEmail.toLowerCase();

  const envLabel = adminEnv === "staging" ? "STAGING" : "PROD";

  const closeModal = () => {
    setStatus(null);
    setCopyEmailError("");
    setDeleteEmailError("");
    setWorkoutIdError("");
    handleClose();
  };

  const submitCopy = async () => {
    setCopyEmailError("");
    if (!copyEmail.trim()) {
      setCopyEmailError("User email is required");
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const result = await handleAdminSubmitCopyWorkoutToUser(copyEmail.trim());
      setStatus(result);
    } finally {
      setBusy(false);
    }
  };

  const openConfirm = () => {
    setDeleteEmailError("");
    setWorkoutIdError("");
    let invalid = false;
    if (!trimmedDeleteEmail) {
      setDeleteEmailError("Target user email is required");
      invalid = true;
    }
    if (!trimmedWorkoutId) {
      setWorkoutIdError("Workout ID is required");
      invalid = true;
    }
    if (invalid) return;
    setConfirmEmail("");
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmEmail("");
  };

  const confirmDelete = async () => {
    if (!armed) return; // belt-and-braces; the button is disabled too.
    setConfirmOpen(false);
    setConfirmEmail("");
    setBusy(true);
    setStatus(null);
    try {
      const result = await handleAdminDeleteWorkoutFromUser(
        trimmedWorkoutId,
        trimmedDeleteEmail
      );
      setStatus(result);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={closeModal}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: 12,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
              backgroundImage: "none",
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
            Admin Workout to User Features
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              Copy a workout to a user, or remove one from their account.
            </Typography>
            <Chip
              size="small"
              label={envLabel}
              color={adminEnv === "staging" ? "warning" : "error"}
              variant="outlined"
              data-testid="admin-workout-env"
            />
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          {status && (
            <Alert
              severity={status.ok ? "success" : "error"}
              sx={{ mb: 2 }}
              data-testid="admin-workout-status"
            >
              {status.message}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Copy workout to user
          </Typography>
          <TextField
            fullWidth
            label="User Email"
            value={copyEmail}
            onChange={(e) => setCopyEmail(e.target.value)}
            error={!!copyEmailError}
            helperText={
              copyEmailError || "Copies this workout onto that user's account."
            }
            margin="dense"
            slotProps={{ htmlInput: { "data-testid": "admin-copy-email" } }}
          />
          <Box sx={{ mt: 1 }}>
            <Button
              onClick={submitCopy}
              variant="contained"
              color="primary"
              disabled={busy}
              data-testid="admin-copy-submit"
            >
              Copy Workout to User
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box
            sx={{
              border: `1px solid ${theme.palette.error.main}`,
              borderRadius: 2,
              p: 2,
              backgroundColor: alpha(theme.palette.error.main, 0.06),
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <WarningAmberIcon color="error" fontSize="small" />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, color: theme.palette.error.main }}
              >
                Danger zone
              </Typography>
            </Box>
            {/* Blast radius stated BEFORE the dialog, as well as inside it. */}
            <Typography variant="caption" component="div" sx={{ mb: 1.5 }}>
              Permanently deletes that workout from the named user's account on{" "}
              <strong>{envLabel}</strong>. This cannot be undone — the confirm is
              armed by typing the target email.
            </Typography>
            <TextField
              fullWidth
              label="Target User Email"
              value={deleteEmail}
              onChange={(e) => setDeleteEmail(e.target.value)}
              error={!!deleteEmailError}
              helperText={deleteEmailError}
              margin="dense"
              slotProps={{ htmlInput: { "data-testid": "admin-delete-email" } }}
            />
            <TextField
              fullWidth
              label="Workout ID to Delete"
              value={workoutToDelete}
              onChange={(e) => setWorkoutToDelete(e.target.value)}
              error={!!workoutIdError}
              helperText={workoutIdError}
              margin="dense"
              slotProps={{
                htmlInput: { "data-testid": "admin-delete-workout-id" },
              }}
            />
            <Box sx={{ mt: 1.5 }}>
              <Button
                onClick={openConfirm}
                variant="contained"
                color="error"
                disabled={busy}
                startIcon={
                  busy ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <WarningAmberIcon />
                  )
                }
                data-testid="admin-delete-open-confirm"
              >
                Delete Workout from User
              </Button>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeModal} color="inherit" sx={{ fontWeight: 500 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={closeConfirm}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              boxShadow: 12,
              border: `1px solid ${theme.palette.divider}`,
              background: theme.palette.background.paper,
              backgroundImage: "none",
            },
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
            Confirm workout delete
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This permanently deletes workout{" "}
              <strong>{trimmedWorkoutId}</strong> from{" "}
              <strong>{trimmedDeleteEmail}</strong> on{" "}
              <strong>{envLabel}</strong>. This cannot be undone.
            </Typography>
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type <strong>{trimmedDeleteEmail}</strong> to confirm.
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={trimmedDeleteEmail}
            autoComplete="off"
            slotProps={{
              htmlInput: { "data-testid": "admin-delete-confirm-email" },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeConfirm} color="inherit" sx={{ fontWeight: 500 }}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={!armed}
            data-testid="admin-delete-confirm"
          >
            Delete workout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminCopyWorkoutToUserModal;
